import { ReactNode, useRef } from 'react';
import clsx from 'clsx';

interface ButtonProps {
  children?: ReactNode;
  onClick?: () => void;
  className?: string;
  htmlType?: 'button' | 'submit' | 'reset';
  type?:
    | 'primary'
    | 'primary-rounded'
    | 'secondary'
    | 'tertiary'
    | 'add'
    | 'addv2'
    | 'link'
    | 'menu'
    | 'edit'
    | 'close'
    | 'default'
    | 'login'
    | 'logout'
    | 'reset'
    | 'strategy'
    | 'finalized'
    | 'warning'
    | 'icon'
    | 'simple';
  disabled?: boolean;
  icon?: React.ReactNode;
}

export function Button({
  children,
  htmlType = 'button',
  type = 'default',
  onClick,
  className,
  disabled,
  icon,
}: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Map to handle different button type classes
  const buttonTypeClasses = {
    primary:
      'border rounded-[10px] px-3 py-1.5 border-primary-100 hover:shadow-button hover:bg-primary-100 hover:text-white',
    'primary-rounded':
      'bg-white text-primary-100 p-1 shadow-button border border-primary-100 hover:bg-primary-100 hover:text-white rounded-full',
    secondary:
      // 'bg-white text-primary-100 p-2 w-48 rounded-lg shadow-button border border-secondary-100 hover:bg-primary-100 hover:text-white',
      'bg-blue text-white p-2 rounded-[5px] text-sm hover:bg-blue/90',
    tertiary:
      'bg-white text-blue p-2 rounded-[10px] shadow-button border border-blue hover:bg-blue hover:text-white',
    add: 'bg-white text-lg text-black p-4 w-full max-w-[220px] rounded-lg border border-primary-100 hover:shadow-button hover:bg-primary-100 hover:text-white',
    addv2:
      'bg-white text-lg text-black p-3 w-full max-w-[220px] rounded-3xl border border-primary-100 hover:shadow-button hover:bg-primary-100 hover:text-white',
    link: 'bg-transparent py-2 text-danger underline',
    menu: 'w-full py-1 my-2 bg-transparent text-base font-normal text-primary-100 tracking-wider active:rounded-md active:font-semibold hover:font-bold focus:rounded-md focus:font-semibold ',
    edit: 'w-8 h-8 bg-transparent text-white hover:bg-blue rounded-full border border-blue flex justify-center items-center',
    close: 'bg-transparent',
    default:
      'bg-white text-primary-100 p-2 w-44 rounded-lg shadow-button border border-secondary-100 hover:bg-primary-100 hover:text-white',
    warning:
      'bg-white text-red-500 p-2 w-44 rounded-lg shadow-button border border-secondary-100 hover:bg-red-500 hover:text-white',
    login:
      'bg-black text-white tracking-wider font-bold p-6 w-100 h-16 rounded-full hover:bg-primary-200 hover:text-white',
    logout:
      'bg-tertiary-600 text-tertiary-500 font-bold px-[30px] py-1.5 rounded-xl hover:bg-primary-100 hover:text-white',
    reset:
      'bg-transparent p-2 font-normal text-secondary-200 underline tracking-wider hover:text-black focus:text-primary-100',
    strategy:
      'bg-white text-primary-100 p-4 w-145 rounded-xl shadow-button border border-secondary-100 hover:bg-primary-100 hover:text-white',
    finalized:
      'bg-checked text-white p-4 w-48 rounded-xl shadow-button border border-checked hover:bg-checked/50 hover:text-primary-100',
    icon: 'bg-transparent w-fit hover:bg-transparent',
    simple:
      'h-5 border rounded-lg p-3 border-primary-100 hover:shadow-button hover:bg-primary-100 hover:text-white',
  };

  // Base classes for all buttons
  const baseClasses =
    'font-bold tracking-wider active:scale-90 flex justify-center items-center text-center';

  // Disabled styles
  const disabledClasses = disabled
    ? 'bg-tertiary-300 text-tertiary-500 cursor-not-allowed hover:bg-tertiary-600 hover:text-tertiary-500'
    : '';

  // Combine classes using clsx
  const combinedClasses = clsx(
    baseClasses,
    buttonTypeClasses[type], // Apply type-specific styles
    disabledClasses,
    className, // Custom classes passed as prop
    icon && 'flex items-center gap-2',
  );

  return (
    <button
      type={htmlType}
      className={combinedClasses}
      onClick={onClick}
      ref={buttonRef}
      disabled={disabled}
    >
      {icon}
      {children}
    </button>
  );
}
